import {InputDate} from '@atomaro/ui-kit';
import {DateWrapper, DiaryHeadRow, DiaryHeadWrapper} from './styles';
import {withMask} from 'use-mask-input';
import {FC, useEffect, useMemo, useRef, useState} from 'react';
import {IDropdownMenuItem} from '@atomaro/ui-kit/components/DropdownMenu/types';
import Button from '@atomaro/ui-kit/components/Button/Button/Button';
import {AddLarge, FiltersIcon} from 'shared/ui/icons';
import {SortSelect} from 'shared/ui/sortSelect';
import Multiselect from '@atomaro/ui-kit/components/Multiselect/Multiselect';
import {useDiaryFilters} from 'features/diary/lib/useDiaryFilters';
import {DiaryFiltersDrawer} from './DiaryFiltersDrawer';
import {COLORS} from 'shared/consts';
import TagItem from '@atomaro/ui-kit/components/Tag/TagItem/TagItem';
import Popover from '@atomaro/ui-kit/components/Popover/Popover';
import {DiaryFiltersRangeItem} from './DiaryFiltersRangeItem';
import {DiaryDictionariesFilter} from './DiaryDictionariesFilter';
import {DiaryCommentFilter} from './DiaryCommentFilter';
import {DiaryAttachmentsFilter} from './DiaryAttachmentsFilter';
import {useResize} from 'app/lib/hooks/useResize';
import {Drawer} from 'shared/ui/drawer';
import {DiaryFiltersAuthors} from './DiaryFiltersAuthors';
import {HivesRow} from 'features/hives/ui/hivesTable/types.ts';
import {MiniCloseIcon} from 'shared/ui/icons/minicloseicon';
import {ArrowDownIcon} from 'shared/ui/icons/arrowDown';

type DiaryHeadProps = {
  hives: HivesRow[];
  onNewEntrieClick: () => void;
};

export const DiaryHead: FC<DiaryHeadProps> = ({hives, onNewEntrieClick}) => {
  const {isScreenMd} = useResize();

  const {
    control,
    isFiltersOpen,
    formValues,
    dictionaryItems,
    settedFiltersCounter,
    shortFilterOpenSelector,
    shortFiltersLabels,
    usersList,
    setIsFiltersOpen,
    onDateChange,
    onDateBlur,
    onRangeChange,
    onResetItems,
    onDictionaryItemClick,
    onDictionaryReset,
    setHasAttachments,
    onDrawerClose,
    onDrawerSubmit,
    onCommentChange,
    onHivesChange,
    openShortFilter,
    closeShortFilter,
    onResetField,
    reset,
    onSortChange,
    onAuthorClick,
    onResetAll,
  } = useDiaryFilters();

  const [hiveItems, setHiveItems] = useState<IDropdownMenuItem[]>([]);
  const sortItems = useRef<IDropdownMenuItem[]>([
    {
      key: 'inspectedAt',
      value: 'Дата осмотра',
    },
    {
      key: 'hive',
      value: 'Улей',
    },
    {
      key: 'author',
      value: 'Сотрудник',
    },
    {
      key: 'frames',
      value: 'Рамки',
    },
    {
      key: 'strength',
      value: 'Сила',
    },
    {
      key: 'brood',
      value: 'Расплод',
    },
    {
      key: 'updatedAt',
      value: 'Обновлено',
    },
  ]);

  useEffect(() => {
    setHiveItems(hives.map((hive) => ({value: `№${hive.number}`, key: hive.id})));
  }, [hives]);

  const showResetAll = useMemo(() => {
    return (
      Boolean(formValues.framesFrom?.trim() || formValues.framesTo?.trim()) ||
      Boolean(formValues.strengthFrom?.trim() || formValues.strengthTo?.trim()) ||
      Boolean(formValues.broodFrom?.trim() || formValues.broodTo?.trim())
    );
  }, [formValues]);

  const hivesMultiSelect = useRef(null);

  return (
    <>
      <DiaryHeadWrapper>
        <DiaryHeadRow>
          <DateWrapper>
            <InputDate
              placeholder="Период осмотра"
              maxDate={new Date()}
              isRange={true}
              ref={withMask(['99.99.9999', '99.99.9999 - 99.99.9999'])}
              onChange={onDateChange}
              onBlur={onDateBlur}
              useInPortal={false}
            />
          </DateWrapper>
          {Boolean(hiveItems?.length) && (
            <DateWrapper>
              <Multiselect
                placeholder="Номер улья"
                placement="bottom"
                items={hiveItems}
                size="m"
                ref={hivesMultiSelect}
                dropdownMenuStyle={{maxHeight: '300px', overflowY: 'auto'}}
                onChange={onHivesChange}
                autocomplete={{
                  enabled: true,
                }}
                useInPortal={false}
              />
            </DateWrapper>
          )}

          {isScreenMd && (
            <>
              <SortSelect
                items={sortItems.current}
                defaultValue="updatedAt"
                value={formValues?.sortBy}
                direction={(formValues?.sort as 'ASC' | 'DESC') || 'DESC'}
                onChange={onSortChange}
              />
              <Button
                label={`Фильтры${settedFiltersCounter ? `: ${settedFiltersCounter}` : ''}`}
                iconPrefix={
                  <FiltersIcon fill={settedFiltersCounter ? COLORS.ACCENT.DEFAULT : undefined} />
                }
                variant="secondary"
                colorScheme={settedFiltersCounter ? 'accent' : 'neutral'}
                onClick={() => setIsFiltersOpen(true)}
              />

              {Boolean(hiveItems?.length) && (
                <Button
                  label="Новая запись"
                  iconPrefix={<AddLarge />}
                  style={{marginLeft: 'auto'}}
                  onClick={onNewEntrieClick}
                />
              )}
            </>
          )}
        </DiaryHeadRow>
        {isScreenMd ? (
          <DiaryHeadRow>
            {usersList && Boolean(usersList?.length) && (
              <Popover
                isOpened={shortFilterOpenSelector === 'author'}
                placement="bottomLeft"
                pointer={false}
                useInPortal={false}
                className="popover_nopadding"
                onClose={() => shortFilterOpenSelector === 'author' && closeShortFilter(true)}
                innerChildren={
                  <DiaryFiltersAuthors
                    authors={usersList}
                    ids={formValues.authorIds || []}
                    onAuthorClick={onAuthorClick}
                    hideHeader
                  />
                }
              >
                <TagItem
                  onClick={() => openShortFilter('author')}
                  onClose={() => onResetField('authorIds')}
                  closable={shortFiltersLabels.author ? true : false}
                >
                  Сотрудник{shortFiltersLabels.author ? `: ${shortFiltersLabels.author}` : ''}
                </TagItem>
              </Popover>
            )}
            <Popover
              isOpened={shortFilterOpenSelector === 'frames'}
              placement="bottomLeft"
              pointer={false}
              useInPortal={false}
              className="popover_nopadding"
              onClose={() => shortFilterOpenSelector === 'frames' && closeShortFilter()}
              innerChildren={
                <DiaryFiltersRangeItem
                  name="frames"
                  control={control}
                  fieldNames={{from: 'framesFrom', to: 'framesTo'}}
                  onChange={onRangeChange}
                  onReset={onResetItems}
                  showReset={Boolean(formValues.framesFrom?.trim() || formValues.framesTo?.trim())}
                  showFooter
                  hideHeader
                  onSubmitClick={() => {
                    closeShortFilter();
                    onDrawerSubmit();
                  }}
                  onCancelClick={() => shortFilterOpenSelector === 'frames' && closeShortFilter()}
                />
              }
            >
              <TagItem
                onClick={() =>
                  shortFilterOpenSelector === 'frames'
                    ? closeShortFilter()
                    : openShortFilter('frames')
                }
                onClose={() => onResetField('frames')}
                closable={shortFiltersLabels.frames ? true : false}
              >
                Рамки{shortFiltersLabels.frames ? `: ${shortFiltersLabels.frames}` : ''}
                {!shortFiltersLabels.strength ? (
                  shortFilterOpenSelector === 'frames' ? (
                    <MiniCloseIcon style={{margin: '0 0 0 5px', pointerEvents: 'none'}} />
                  ) : (
                    <ArrowDownIcon style={{margin: '0 0 0 5px', pointerEvents: 'none'}} />
                  )
                ) : (
                  ''
                )}
              </TagItem>
            </Popover>
            <Popover
              isOpened={shortFilterOpenSelector === 'strength'}
              placement="bottomLeft"
              pointer={false}
              useInPortal={false}
              className="popover_nopadding"
              onClose={() => shortFilterOpenSelector === 'strength' && closeShortFilter()}
              innerChildren={
                <DiaryFiltersRangeItem
                  name="strength"
                  control={control}
                  fieldNames={{from: 'strengthFrom', to: 'strengthTo'}}
                  onChange={onRangeChange}
                  onReset={onResetItems}
                  showReset={Boolean(
                    formValues.strengthFrom?.trim() || formValues.strengthTo?.trim()
                  )}
                  showFooter
                  hideHeader
                  onSubmitClick={() => {
                    closeShortFilter();
                    onDrawerSubmit();
                  }}
                  onCancelClick={() => shortFilterOpenSelector === 'strength' && closeShortFilter()}
                />
              }
            >
              <TagItem
                onClick={() =>
                  shortFilterOpenSelector === 'strength'
                    ? closeShortFilter()
                    : openShortFilter('strength')
                }
                onClose={() => onResetField('strength')}
                closable={shortFiltersLabels.strength ? true : false}
              >
                Сила{shortFiltersLabels.strength ? `: ${shortFiltersLabels.strength}` : ''}
                {!shortFiltersLabels.strength ? (
                  shortFilterOpenSelector === 'strength' ? (
                    <MiniCloseIcon style={{margin: '0 0 0 5px', pointerEvents: 'none'}} />
                  ) : (
                    <ArrowDownIcon style={{margin: '0 0 0 5px', pointerEvents: 'none'}} />
                  )
                ) : (
                  ''
                )}
              </TagItem>
            </Popover>
            <Popover
              isOpened={shortFilterOpenSelector === 'brood'}
              placement="bottomLeft"
              pointer={false}
              className="popover_nopadding"
              onClose={() => shortFilterOpenSelector === 'brood' && closeShortFilter()}
              innerChildren={
                <DiaryFiltersRangeItem
                  name="brood"
                  control={control}
                  fieldNames={{from: 'broodFrom', to: 'broodTo'}}
                  onChange={onRangeChange}
                  onReset={onResetItems}
                  showReset={Boolean(formValues.broodFrom?.trim() || formValues.broodTo?.trim())}
                  showFooter
                  hideHeader
                  onSubmitClick={() => {
                    closeShortFilter();
                    onDrawerSubmit();
                  }}
                  onCancelClick={() => shortFilterOpenSelector === 'brood' && closeShortFilter()}
                />
              }
            >
              <TagItem
                onClick={() =>
                  shortFilterOpenSelector === 'brood'
                    ? closeShortFilter()
                    : openShortFilter('brood')
                }
                onClose={() => onResetField('brood')}
                closable={shortFiltersLabels.brood ? true : false}
              >
                Расплод{shortFiltersLabels.brood ? `: ${shortFiltersLabels.brood}` : ''}
                {!shortFiltersLabels.brood ? (
                  shortFilterOpenSelector === 'brood' ? (
                    <MiniCloseIcon style={{margin: '0 0 0 5px', pointerEvents: 'none'}} />
                  ) : (
                    <ArrowDownIcon style={{margin: '0 0 0 5px', pointerEvents: 'none'}} />
                  )
                ) : (
                  ''
                )}
              </TagItem>
            </Popover>
            <Popover
              isOpened={shortFilterOpenSelector === 'dictionariesIds'}
              placement="bottomLeft"
              pointer={false}
              className="popover_nopadding"
              onClose={() => {
                if (shortFilterOpenSelector === 'dictionariesIds') {
                  onDrawerSubmit(true);
                  closeShortFilter();
                }
              }}
              innerChildren={
                <DiaryDictionariesFilter
                  formValues={formValues}
                  onDictionaryReset={onDictionaryReset}
                  dictionaryItems={dictionaryItems}
                  isDrawer={false}
                  hideHeader
                  onDictionaryItemClick={onDictionaryItemClick}
                />
              }
            >
              <TagItem
                onClick={() =>
                  shortFilterOpenSelector === 'dictionariesIds'
                    ? closeShortFilter()
                    : openShortFilter('dictionariesIds')
                }
                onClose={() => onResetField('dictionariesIds')}
                closable={shortFiltersLabels.dictionariesIds ? true : false}
              >
                Состояние
                {shortFiltersLabels.dictionariesIds
                  ? `: ${shortFiltersLabels.dictionariesIds}`
                  : ''}
                {!shortFiltersLabels.dictionariesIds ? (
                  shortFilterOpenSelector === 'dictionariesIds' ? (
                    <MiniCloseIcon style={{margin: '0 0 0 5px', pointerEvents: 'none'}} />
                  ) : (
                    <ArrowDownIcon style={{margin: '0 0 0 5px', pointerEvents: 'none'}} />
                  )
                ) : (
                  ''
                )}
              </TagItem>
            </Popover>
            <Popover
              isOpened={shortFilterOpenSelector === 'commentKeyWords'}
              placement="bottomLeft"
              pointer={false}
              className="popover_nopadding"
              onClose={() =>
                shortFilterOpenSelector === 'commentKeyWords' && closeShortFilter(true)
              }
              innerChildren={
                <DiaryCommentFilter
                  formValues={formValues}
                  control={control}
                  onCommentChange={onCommentChange}
                  showFooter
                  hideHeader
                  onCancelClick={() =>
                    shortFilterOpenSelector === 'commentKeyWords' && closeShortFilter()
                  }
                  onSubmitClick={() => {
                    closeShortFilter(true);
                  }}
                />
              }
            >
              <TagItem
                onClick={() =>
                  shortFilterOpenSelector === 'commentKeyWords'
                    ? closeShortFilter()
                    : openShortFilter('commentKeyWords')
                }
                onClose={() => onResetField('commentKeyWords')}
                closable={shortFiltersLabels.commentKeyWords ? true : false}
              >
                Комментарий
                {shortFiltersLabels.commentKeyWords
                  ? `: ${shortFiltersLabels.commentKeyWords}`
                  : ''}
                {!shortFiltersLabels.commentKeyWords ? (
                  shortFilterOpenSelector === 'commentKeyWords' ? (
                    <MiniCloseIcon style={{margin: '0 0 0 5px', pointerEvents: 'none'}} />
                  ) : (
                    <ArrowDownIcon style={{margin: '0 0 0 5px', pointerEvents: 'none'}} />
                  )
                ) : (
                  ''
                )}
              </TagItem>
            </Popover>
            {/* <Popover
            isOpened={shortFilterOpenSelector === 'hasAttachments'}
            placement="bottomLeft"
            pointer={false}
            className="popover_nopadding"
            onClose={() => shortFilterOpenSelector === 'hasAttachments' && closeShortFilter()}
            innerChildren={
            <DiaryAttachmentsFilter 
              formValues={formValues}
              hideHead
              setHasAttachments={(value: boolean) => {
                setHasAttachments(value);
                closeShortFilter();
                onDrawerSubmit();
              }}
            />}
          >
            <TagItem 
              onClick={() => openShortFilter('hasAttachments')}
              onClose={() => onResetField('hasAttachments')}
              closable={shortFiltersLabels.hasAttachments ? true : false}
            >
              Вложения{shortFiltersLabels.hasAttachments ? `: ${shortFiltersLabels.hasAttachments}` : ''}
            </TagItem>
          </Popover> */}

            {Boolean(showResetAll && settedFiltersCounter) && (
              <TagItem onClick={() => onResetAll()}>Сбросить всё</TagItem>
            )}
          </DiaryHeadRow>
        ) : (
          <DiaryHeadRow $overflow="auto hidden">
            <SortSelect
              items={sortItems.current}
              isMobile
              defaultValue="updatedAt"
              value={formValues?.sortBy}
              direction={(formValues?.sort as 'ASC' | 'DESC') || 'DESC'}
              onChange={onSortChange}
            />
            <TagItem onClick={() => setIsFiltersOpen(true)}>
              <FiltersIcon />
            </TagItem>
            <TagItem
              onClick={() => openShortFilter('author')}
              onClose={() => onResetField('authorIds')}
              closable={shortFiltersLabels.author ? true : false}
            >
              Сотрудник{shortFiltersLabels.author ? `: ${shortFiltersLabels.author}` : ''}
            </TagItem>
            <TagItem
              onClick={() => openShortFilter('frames')}
              onClose={() => onResetField('frames')}
              closable={shortFiltersLabels.frames ? true : false}
            >
              Рамки{shortFiltersLabels.frames ? `: ${shortFiltersLabels.frames}` : ''}
            </TagItem>
            <TagItem
              onClick={() => openShortFilter('strength')}
              onClose={() => onResetField('strength')}
              closable={shortFiltersLabels.strength ? true : false}
            >
              Сила{shortFiltersLabels.strength ? `: ${shortFiltersLabels.strength}` : ''}
            </TagItem>
            <TagItem
              onClick={() => openShortFilter('brood')}
              onClose={() => onResetField('brood')}
              closable={shortFiltersLabels.brood ? true : false}
            >
              Расплод{shortFiltersLabels.brood ? `: ${shortFiltersLabels.brood}` : ''}
            </TagItem>
            <TagItem
              onClick={() => openShortFilter('dictionariesIds')}
              onClose={() => onResetField('dictionariesIds')}
              closable={shortFiltersLabels.dictionariesIds ? true : false}
            >
              Состояние
              {shortFiltersLabels.dictionariesIds ? `: ${shortFiltersLabels.dictionariesIds}` : ''}
            </TagItem>
            <TagItem
              onClick={() => openShortFilter('commentKeyWords')}
              onClose={() => onResetField('commentKeyWords')}
              closable={shortFiltersLabels.commentKeyWords ? true : false}
            >
              Комментарий
              {shortFiltersLabels.commentKeyWords ? `: ${shortFiltersLabels.commentKeyWords}` : ''}
            </TagItem>
            {/* <TagItem 
            onClick={() => openShortFilter('hasAttachments')}
            onClose={() => onResetField('hasAttachments')}
            closable={shortFiltersLabels.hasAttachments ? true : false}
          >
            Вложения{shortFiltersLabels.hasAttachments ? `: ${shortFiltersLabels.hasAttachments}` : ''}
          </TagItem> */}
            {Boolean(showResetAll && settedFiltersCounter) && (
              <TagItem onClick={() => onResetAll()}>Сбросить всё</TagItem>
            )}

            {usersList && Boolean(usersList?.length) && (
              <Drawer
                isOpen={shortFilterOpenSelector === 'author'}
                position="bottom"
                onClose={() => shortFilterOpenSelector === 'author' && closeShortFilter()}
              >
                <DiaryFiltersAuthors
                  authors={usersList}
                  ids={formValues.authorIds || []}
                  onAuthorClick={onAuthorClick}
                />
              </Drawer>
            )}
            <Drawer
              isOpen={shortFilterOpenSelector === 'frames'}
              position="bottom"
              onClose={() => shortFilterOpenSelector === 'frames' && closeShortFilter()}
            >
              <DiaryFiltersRangeItem
                name="frames"
                control={control}
                fieldNames={{from: 'framesFrom', to: 'framesTo'}}
                onChange={onRangeChange}
                onReset={onResetItems}
                showReset={Boolean(formValues.framesFrom?.trim() || formValues.framesTo?.trim())}
                showFooter
                onSubmitClick={() => {
                  closeShortFilter();
                  onDrawerSubmit();
                }}
                onCancelClick={() => shortFilterOpenSelector === 'frames' && closeShortFilter()}
              />
            </Drawer>
            <Drawer
              isOpen={shortFilterOpenSelector === 'strength'}
              position="bottom"
              onClose={() => shortFilterOpenSelector === 'strength' && closeShortFilter()}
            >
              <DiaryFiltersRangeItem
                name="strength"
                control={control}
                fieldNames={{from: 'strengthFrom', to: 'strengthTo'}}
                onChange={onRangeChange}
                onReset={onResetItems}
                showReset={Boolean(
                  formValues.strengthFrom?.trim() || formValues.strengthTo?.trim()
                )}
                showFooter
                onSubmitClick={() => {
                  closeShortFilter();
                  onDrawerSubmit();
                }}
                onCancelClick={() => shortFilterOpenSelector === 'strength' && closeShortFilter()}
              />
            </Drawer>
            <Drawer
              isOpen={shortFilterOpenSelector === 'brood'}
              position="bottom"
              onClose={() => shortFilterOpenSelector === 'brood' && closeShortFilter()}
            >
              <DiaryFiltersRangeItem
                name="brood"
                control={control}
                fieldNames={{from: 'broodFrom', to: 'broodTo'}}
                onChange={onRangeChange}
                onReset={onResetItems}
                showReset={Boolean(formValues.broodFrom?.trim() || formValues.broodTo?.trim())}
                showFooter
                onSubmitClick={() => {
                  closeShortFilter();
                  onDrawerSubmit();
                }}
                onCancelClick={() => shortFilterOpenSelector === 'brood' && closeShortFilter()}
              />
            </Drawer>
            <Drawer
              isOpen={shortFilterOpenSelector === 'dictionariesIds'}
              position="bottom"
              onClose={() => {
                if (shortFilterOpenSelector === 'dictionariesIds') {
                  onDrawerSubmit(true);
                  closeShortFilter();
                }
              }}
            >
              <DiaryDictionariesFilter
                formValues={formValues}
                onDictionaryReset={onDictionaryReset}
                dictionaryItems={dictionaryItems}
                isDrawer={false}
                onDictionaryItemClick={onDictionaryItemClick}
              />
            </Drawer>
            <Drawer
              isOpen={shortFilterOpenSelector === 'commentKeyWords'}
              position="bottom"
              onClose={() => {
                if (shortFilterOpenSelector === 'commentKeyWords') {
                  closeShortFilter(true);
                }
              }}
            >
              <DiaryCommentFilter
                formValues={formValues}
                control={control}
                onCommentChange={onCommentChange}
                showFooter
                onCancelClick={() =>
                  shortFilterOpenSelector === 'commentKeyWords' && closeShortFilter()
                }
                onSubmitClick={() => {
                  closeShortFilter(true);
                }}
              />
            </Drawer>
            <Drawer
              isOpen={shortFilterOpenSelector === 'hasAttachments'}
              position="bottom"
              onClose={() => shortFilterOpenSelector === 'hasAttachments' && closeShortFilter()}
            >
              <DiaryAttachmentsFilter
                formValues={formValues}
                setHasAttachments={(value: boolean) => {
                  setHasAttachments(value);
                  closeShortFilter();
                  onDrawerSubmit();
                }}
              />
            </Drawer>
          </DiaryHeadRow>
        )}

        <DiaryFiltersDrawer
          isOpen={isFiltersOpen}
          usersList={usersList}
          control={control}
          formValues={formValues}
          dictionaryItems={dictionaryItems}
          isMobile={!isScreenMd}
          showResetAll={showResetAll}
          onClose={onDrawerClose}
          onRangeChange={onRangeChange}
          onResetItems={onResetItems}
          onDictionaryItemClick={onDictionaryItemClick}
          onDictionaryReset={onDictionaryReset}
          setHasAttachments={setHasAttachments}
          onSubmit={onDrawerSubmit}
          onCommentChange={onCommentChange}
          onAllReset={reset}
          onAuthorClick={onAuthorClick}
        />
      </DiaryHeadWrapper>
    </>
  );
};
